import { HtmlString } from '@breezy/shared'
import DOMPurify from 'dompurify'

export const stripHtml = (html: HtmlString): string => {
  const preProcessed = (html as string)
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<\/p>/gi, ' ')
    .replace(/<p>/gi, '')
  return DOMPurify.sanitize(preProcessed, { ALLOWED_TAGS: [] })
}

/**
 * Extracts plain text from HTML, collapsing whitespace and trimming.
 * - Reuses stripHtml to remove tags and sanitize.
 * - Normalizes non-breaking spaces to regular spaces.
 * - Collapses internal whitespace to single spaces.
 * - Trims leading/trailing whitespace.
 */
export const extractTextFromHtml = (html: HtmlString | string | null | undefined): string => {
  const raw = (html ?? '') as HtmlString
  const withoutTags = stripHtml(raw)
  return withoutTags
    .replace(/\u00A0/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

/**
 * Returns true iff the provided HTML has meaningful (non-empty) text content.
 */
export const isHtmlMeaningful = (html: HtmlString | string | null | undefined): boolean => {
  return extractTextFromHtml(html).length > 0
}

/**
 * Builds a human-friendly preview from HTML/plain text.
 * - Extracts text from HTML.
 * - Optionally collapses whitespace (default: true).
 * - Truncates to maxLength (default: 80) and appends an ellipsis when truncated.
 */
export const getHtmlPreview = (
  html: HtmlString | string,
  options?: { maxLength?: number; collapseWhitespace?: boolean },
): string => {
  const maxLength = options?.maxLength ?? 80
  const collapse = options?.collapseWhitespace ?? true

  let text = extractTextFromHtml(html)
  if (collapse) {
    text = text.replace(/\s+/g, ' ').trim()
  }

  if (text.length <= maxLength) {
    return text
  }

  const truncated = text.slice(0, maxLength).trim()
  return `${truncated}\u2026`
}
